import React from 'react';
import { FileText, Upload, Pencil, Trash2, Download, AlertCircle } from 'lucide-react';
import { DOCUMENT_TYPES } from './vaultConstants';
import { formatFileSize, formatDate, getVisibilityBadge } from './vaultUtils';

export function DocumentsTab({ documents, canModify, onUploadClick, onEdit, onDelete, onDownload }) {
  return (
    <>
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-blue-800">Document Access Levels</p>
          <p className="text-blue-700">
            Documents can be <strong>Private</strong> (only you), <strong>Executors Only</strong> (you + designated executors), or <strong>All Members</strong>.
          </p>
        </div>
      </div>

      {documents.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300" data-testid="vault-empty">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No documents yet</h3>
          <p className="text-gray-600 mb-4">Upload your first document to get started</p>
          <button onClick={onUploadClick} className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors">
            <Upload className="w-4 h-4" />Upload Document
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="vault-documents-grid">
          {documents.map((doc) => (
            <div key={doc.document_id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow" data-testid={`vault-document-${doc.document_id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-gray-900 truncate" title={doc.title}>{doc.title}</h4>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${DOCUMENT_TYPES[doc.document_type]?.color || DOCUMENT_TYPES.other.color}`}>
                        {DOCUMENT_TYPES[doc.document_type]?.label || 'Other'}
                      </span>
                      {getVisibilityBadge(doc.visibility || 'executors')}
                      {!doc.storage_path && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" title="This document was stored before our secure cloud storage upgrade and may need to be re-uploaded." data-testid={`needs-reupload-${doc.document_id}`}>
                          <AlertCircle className="w-3 h-3" />Re-upload needed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {doc.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{doc.description}</p>}
              <div className="text-xs text-gray-500 space-y-1 mb-3">
                <p>By {doc.uploader_name}</p>
                <p>{formatDate(doc.uploaded_at)} · {formatFileSize(doc.file_size)}</p>
              </div>
              <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                <button onClick={() => onDownload(doc)} className="flex items-center gap-1 px-3 py-1.5 text-sm text-teal-600 hover:bg-teal-50 rounded-md transition-colors" data-testid={`download-${doc.document_id}`}>
                  <Download className="w-4 h-4" />Download
                </button>
                {canModify(doc) && (
                  <>
                    <button onClick={() => onEdit(doc)} className="flex items-center gap-1 px-2 py-1.5 text-xs sm:text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" title="Edit" data-testid={`edit-${doc.document_id}`}>
                      <Pencil className="w-4 h-4" />
                      <span className="sm:hidden">Edit</span>
                    </button>
                    <button onClick={() => onDelete(doc)} className="flex items-center gap-1 px-2 py-1.5 text-xs sm:text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors" title="Delete" data-testid={`delete-${doc.document_id}`}>
                      <Trash2 className="w-4 h-4" />
                      <span className="sm:hidden">Delete</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
